import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl, storageUrl, withStorageFallback, normalizeMediaUrl } from '../utils/apiBase';
import { formatSize } from '../utils/formatters';
import toast from 'react-hot-toast';
import MapComponent from '../components/MapComponent';

// Utility function to add timeout to fetch requests
const fetchWithTimeout = (url, options = {}, timeout = 10000) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeout)
    )
  ]);
};

const PropertyDetailPage = ({ previewProperty: previewPropertyProp, previewOnly: previewOnlyProp = false, previewBackTo: previewBackToProp = null }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const previewProperty = previewPropertyProp ?? location.state?.previewProperty ?? null;
  const previewOnly = previewOnlyProp ?? location.state?.previewOnly ?? false;
  const previewBackTo = previewBackToProp ?? location.state?.previewBackTo ?? null;
  const isPreview = Boolean(previewProperty);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [personalNote, setPersonalNote] = useState('');
  const [personalNoteSaving, setPersonalNoteSaving] = useState(false);
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: ''
  });
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({
    reason: 'sold',
    details: ''
  });
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const lastFetchedIdRef = useRef(null);
  const isFetchingRef = useRef(false);

  const coords = useMemo(() => {
    const lat = parseFloat(property?.latitude);
    const lng = parseFloat(property?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
  }, [property?.latitude, property?.longitude]);

  const directionsLinks = useMemo(() => {
    if (!coords) return null;
    const { lat, lng } = coords;
    return {
      google: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
      waze: `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,
    };
  }, [coords]);

  const homesUrlByLanguage = {
    en: 'https://salesign.app/homes/',
    ru: 'https://salesign.app/homes/',
    de: 'https://salesign.app/de/hauser/',
    es: 'https://salesign.app/es/casa/',
    pt: 'https://salesign.app/pt/casas/',
    zh: 'https://salesign.app/zh/%e6%88%bf%e5%ad%90/',
    fr: 'https://salesign.app/fr/maisons/',
    ar: 'https://salesign.app/ar/%d9%85%d9%86%d8%b2%d9%84/',
  };
  const homesUrl = homesUrlByLanguage[i18n.language] ?? homesUrlByLanguage.en;
  const isLandLot = property?.property_category === 'land_lot';

  const fetchProperty = useCallback(async () => {
    try {
      isFetchingRef.current = true;
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetchWithTimeout(apiUrl(`/properties/${id}`), {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }, 10000);

      const data = await response.json();
      
      if (data.success) {
        setProperty(data.data);
        setIsFavorited(data.is_favorited || false);
        setContactForm(prev => ({
          ...prev,
          subject: t('propertyDetailPage.subjectPrefix', { title: data.data.title })
        }));
        lastFetchedIdRef.current = id;
      } else {
        toast.error(t('propertyDetailPage.propertyNotFoundToast'));
        navigate('/preview');
        lastFetchedIdRef.current = null;
      }
    } catch (error) {
      console.error('Error fetching property:', error);
      if (error.message === 'Request timeout') {
        toast.error(t('common.requestTimedOut'));
      } else {
        toast.error(t('propertyDetailPage.failedToLoadProperty'));
      }
      navigate('/preview');
      lastFetchedIdRef.current = null;
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    if (!isPreview) return;
    setProperty(previewProperty);
    setLoading(false);
    if (previewProperty?.title) {
      setContactForm((prev) => ({
        ...prev,
        subject: t('propertyDetailPage.subjectPrefix', { title: previewProperty.title }),
      }));
    }
  }, [isPreview, previewProperty, t]);

  useEffect(() => {
    if (isPreview) {
      return;
    }
    if (!isAuthenticated || user?.role !== 'land_seeker') {
      navigate('/login', { state: { role: 'land_seeker' } });
      return;
    }

    if (isFetchingRef.current || lastFetchedIdRef.current === id) {
      return;
    }

    fetchProperty();
  }, [id, isAuthenticated, user?.role, navigate, fetchProperty, isPreview]);

  const normalizedWhatsapp = property?.whatsapp ? property.whatsapp.trim() : '';

  const handlePreviewOnlyAction = useCallback(() => {
    toast(t('propertyDetailPage.previewOnlyToast'));
  }, [t]);

  const handleContact = (method) => {
    if (!property) return;
    if (previewOnly || isPreview) {
      handlePreviewOnlyAction();
      return;
    }

    if (method === 'call' && property.phone) {
      window.open(`tel:${property.phone}`);
    } else if (method === 'whatsapp' && normalizedWhatsapp) {
      const message = t('propertyDetailPage.whatsappMessage', { title: property.title });
      window.open(`https://wa.me/${normalizedWhatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!contactForm.message.trim()) return;
    if (previewOnly || isPreview) {
      handlePreviewOnlyAction();
      return;
    }

    setIsSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithTimeout(apiUrl(`/properties/${property.id}/contact`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          subject: contactForm.subject, 
          message: contactForm.message 
        }),
      }, 10000);

      const data = await response.json();
      
      if (data.success) {
        toast.success(t('propertyDetailPage.messageSent'));
        setShowContactForm(false);
        setContactForm(prev => ({ ...prev, message: '' }));
      } else {
        toast.error(data.message || t('propertyDetailPage.failedToSendMessage'));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      if (error.message === 'Request timeout') {
        toast.error(t('common.requestTimedOut'));
      } else {
        toast.error(t('propertyDetailPage.failedToSendMessage'));
      }
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleShare = () => {
    if (previewOnly || isPreview) {
      handlePreviewOnlyAction();
      return;
    }
    const shareText = t('propertyDetailPage.shareText', { title: property.title });
    const shareUrl = window.location.href;
    
    // Create share data
    const shareData = {
      title: property.title,
      text: `${property.title} - ${property.description ? property.description.substring(0, 100) + '...' : t('propertyDetailPage.propertyListingFallback')}`,
      url: shareUrl
    };

    // Try native sharing first (mobile)
    if (navigator.share) {
      navigator.share(shareData).catch(console.error);
    } else {
      // Fallback to copy link
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success(t('propertyDetailPage.linkCopied'));
    }
  };

  const handleToggleFavorite = async () => {
    if (previewOnly || isPreview) {
      handlePreviewOnlyAction();
      return;
    }
    if (!isAuthenticated) {
      toast.error(t('propertyDetailPage.loginToFavorite'));
      navigate('/login', { state: { role: 'land_seeker' } });
      return;
    }

    setIsTogglingFavorite(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithTimeout(apiUrl(`/properties/${property.id}/favorite`), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }, 10000);

      const data = await response.json();
      
      if (data.success) {
        setIsFavorited(data.data.is_favorited);
        toast.success(data.message);
      } else {
        toast.error(data.message || t('propertyDetailPage.failedToUpdateFavorite'));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(t('propertyDetailPage.failedToUpdateFavorite'));
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  // Load personal note when property is favorited
  useEffect(() => {
    if (isPreview || previewOnly || !isAuthenticated || !isFavorited || !property?.id) {
      if (!isFavorited) setPersonalNote('');
      return;
    }
    const token = localStorage.getItem('token');
    fetchWithTimeout(apiUrl(`/favorites/note/${property.id}`), {
      headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
    }, 8000)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data?.note != null) setPersonalNote(data.data.note || '');
      })
      .catch(() => {});
  }, [property?.id, isFavorited, isAuthenticated, isPreview, previewOnly]);

  const savePersonalNote = useCallback(async () => {
    if (isPreview || previewOnly || !property?.id || !isFavorited) return;
    setPersonalNoteSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithTimeout(apiUrl(`/favorites/note/${property.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ note: personalNote }),
      }, 8000);
      const data = await response.json();
      if (!data.success) toast.error(t('propertyDetailPage.failedToSaveNote'));
      else toast.success(t('propertyDetailPage.noteSaved'));
    } catch (e) {
      toast.error(t('propertyDetailPage.failedToSaveNote'));
    } finally {
      setPersonalNoteSaving(false);
    }
  }, [property?.id, personalNote, isFavorited, isPreview, previewOnly, t]);

  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReportForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (previewOnly || isPreview) {
      handlePreviewOnlyAction();
      return;
    }
    if (!property) return;

    setIsSubmittingReport(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithTimeout(apiUrl(`/properties/${property.id}/report`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(reportForm),
      }, 10000);

      const data = await response.json();
      if (data.success) {
        toast.success(data.message || t('propertyDetailPage.reportSubmitted'));
        setShowReportModal(false);
        setReportForm({ reason: 'sold', details: '' });
      } else {
        toast.error(data.message || t('propertyDetailPage.failedToSubmitReport'));
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(error.message === 'Request timeout' ? t('propertyDetailPage.reportTimedOut') : t('propertyDetailPage.failedToSubmitReport'));
    } finally {
      setIsSubmittingReport(false);
    }
  };

  // Helper function to determine if media is an image or video
  const isImage = (media) => {
    if (!media) return false;
    
    // Check file_type if available
    if (media.file_type) {
      return media.file_type === 'image';
    }
    
    // Check file extension
    if (media.image_url || media.file_path) {
      const url = media.image_url || media.file_path;
      const extension = url.split('.').pop()?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension);
    }
    
    return false;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(i18n.language || 'en', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatBoolean = (value) => {
    if (value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true' || String(value).toLowerCase() === 'yes' || String(value).toLowerCase() === 'on') {
      return t('propertyDetailPage.yes');
    }
    if (value === false || value === 0 || value === '0' || String(value).toLowerCase() === 'false' || String(value).toLowerCase() === 'no' || String(value).toLowerCase() === 'off') {
      return t('propertyDetailPage.no');
    }
    return value != null && value !== '' ? String(value) : '—';
  };

  // Lightbox helpers (declare before early returns to satisfy hooks rules)
  const primaryImage = property?.images?.find(img => img.is_primary) || property?.images?.[0];
  const imageList = (property?.images || []).filter(isImage);

  function openLightbox(index) {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  }

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  const gotoPrev = useCallback(() => {
    if (imageList.length === 0) return;
    setLightboxIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
  }, [imageList.length]);

  const gotoNext = useCallback(() => {
    if (imageList.length === 0) return;
    setLightboxIndex((prev) => (prev + 1) % imageList.length);
  }, [imageList.length]);

  useEffect(() => {
    if (!isLightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') gotoPrev();
      if (e.key === 'ArrowRight') gotoNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLightboxOpen, imageList.length, gotoPrev, gotoNext, closeLightbox]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('propertyDetailPage.loadingProperty')}</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">🏠</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('propertyDetailPage.propertyNotFoundTitle')}</h3>
          <p className="text-gray-600 mb-4">{t('propertyDetailPage.propertyNotFoundBody')}</p>
          <button
            onClick={() => {
              if (previewOnly && previewBackTo) {
                navigate(previewBackTo);
              } else if (previewOnly) {
                navigate(-1);
              } else {
                navigate('/preview');
              }
            }}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('propertyDetailPage.backToProperties')}
          </button>
        </div>
      </div>
    );
  }

  

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => {
                  if (previewOnly && previewBackTo) {
                    navigate(previewBackTo);
                  } else if (previewOnly) {
                    navigate(-1);
                  } else {
                    navigate('/preview');
                  }
                }}
                className="text-gray-500 hover:text-gray-700 shrink-0 mt-0.5"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{property.title}</h1>
                <p className="text-gray-600 break-words">{property.address}, {property.city}</p>
                {property.views_count !== undefined && (
                  <p className="text-sm text-gray-500 mt-1">
                    👁️ {property.views_count} {property.views_count === 1 ? t('propertyDetailPage.view') : t('propertyDetailPage.views')}
                  </p>
                )}
              </div>
            </div>
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end sm:gap-3 lg:w-auto shrink-0">
              <button
                type="button"
                onClick={handleToggleFavorite}
                disabled={isTogglingFavorite}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isFavorited
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } disabled:opacity-50`}
              >
                {isTogglingFavorite ? t('common.loading') : (isFavorited ? `❤️ ${t('propertyDetailPage.favorited')}` : `🤍 ${t('propertyDetailPage.addToFavorites')}`)}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="bg-purple-600 text-white px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                📤 {t('propertyDetailPage.share')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/preferences')}
                className="bg-green-600 text-white px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors col-span-2 sm:col-span-1"
              >
                {t('propertyDetailPage.managePreferences')}
              </button>
              <button
                type="button"
                onClick={() => setShowReportModal(true)}
                className="border border-red-300 text-red-700 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors col-span-2 sm:col-span-1"
              >
                {t('propertyDetailPage.reportListing')}
              </button>
            </div>
          </div>
          {isFavorited && !isPreview && !previewOnly && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📝 {t('propertyDetailPage.personalNote')}
              </label>
              <textarea
                value={personalNote}
                onChange={(e) => setPersonalNote(e.target.value)}
                onBlur={savePersonalNote}
                placeholder={t('propertyDetailPage.personalNotePlaceholder')}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={savePersonalNote}
                  disabled={personalNoteSaving}
                  className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {personalNoteSaving ? t('common.loading') : t('propertyDetailPage.saveNote')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Main Image/Video */}
            {primaryImage && (
              <div className="mb-6">
                {isImage(primaryImage) ? (
                  <img
                    src={normalizeMediaUrl(primaryImage.image_url) || storageUrl(primaryImage.file_path)}
                    alt={property.title}
                    className="w-full h-96 object-cover rounded-lg cursor-zoom-in"
                    onClick={() => openLightbox(imageList.findIndex(i => i.id === primaryImage.id))}
                    onError={(e) => {
                      console.error('Image failed to load:', e.target.src);
                      if (!e.target.dataset.fallbackTried) {
                        e.target.dataset.fallbackTried = '1';
                        e.target.src = withStorageFallback(e.target.src);
                        return;
                      }
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <video
                    src={normalizeMediaUrl(primaryImage.image_url) || storageUrl(primaryImage.file_path)}
                    controls
                    className="w-full h-96 object-cover rounded-lg"
                    onError={(e) => {
                      console.error('Video failed to load:', e.target.src);
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </div>
            )}
            
            {/* Image Gallery */}
            {property.images && property.images.length > 1 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">{t('propertyDetailPage.moreImages')}</h3>
                <div className="grid grid-cols-4 gap-4">
                  {property.images.slice(1, 9).map((image, index) => (
                    <div key={index} className="aspect-square">
                      {isImage(image) ? (
                        <img
                          src={normalizeMediaUrl(image.image_url) || storageUrl(image.file_path)}
                          alt={`${property.title} ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg cursor-zoom-in"
                          onClick={() => openLightbox(imageList.findIndex(i => i.id === image.id))}
                          onError={(e) => {
                            console.error('Gallery image failed to load:', e.target.src);
                            if (!e.target.dataset.fallbackTried) {
                              e.target.dataset.fallbackTried = '1';
                              e.target.src = withStorageFallback(e.target.src);
                              return;
                            }
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <video
                          src={normalizeMediaUrl(image.image_url) || storageUrl(image.file_path)}
                          className="w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            console.error('Gallery video failed to load:', e.target.src);
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Property Details */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">{t('propertyDetailPage.propertyDetails')}</h2>

              {property.client_reference != null && property.client_reference !== '' && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-1">{t('propertyDetailPage.clientReference')}</h3>
                  <p className="text-gray-700">{property.client_reference}</p>
                </div>
              )}

              {property.property_category && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-1">{t('propertyDetailPage.propertyCategory')}</h3>
                  <p className="text-gray-700">{t(`propertyDetailPage.categories.${property.property_category}`) || property.property_category}</p>
                </div>
              )}

              {property.sale_type_detail && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-1">{t('propertyDetailPage.saleTypeDetail')}</h3>
                  <p className="text-gray-700">{t(`propertyDetailPage.saleTypes.${property.sale_type_detail}`) || property.sale_type_detail}</p>
                </div>
              )}

              {property.description && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-2">{t('propertyDetailPage.description')}</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{property.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6 mb-4">
                {property.size != null && property.size !== '' && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.lotSize')}</h3>
                    <p className="text-gray-700 text-lg">{formatSize(property.size, property.size_unit)}</p>
                  </div>
                )}
                {property.price != null && property.price !== '' && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.price')}</h3>
                    <p className="text-green-600 font-bold text-2xl">{formatPrice(property.price)}</p>
                  </div>
                )}
                {property.region && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.region')}</h3>
                    <p className="text-gray-700">{property.region}</p>
                  </div>
                )}
                {property.property_type && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.type')}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      property.property_type === 'sale'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {property.property_type === 'sale' ? `🏠 ${t('propertyDetailPage.forSale')}` : `🏠 ${t('propertyDetailPage.forRent')}`}
                    </span>
                  </div>
                )}
              </div>

              {/* Residential details (apartments & houses): Lot size, Bedrooms, Bathrooms, Living area, Year built */}
              {['apartment', 'house'].includes(property.property_category) && (
                <div className="grid grid-cols-2 gap-6 border-t pt-6">
                  <h3 className="col-span-2 font-semibold mb-1 text-gray-900">{t('propertyDetailPage.residentialDetails')}</h3>
                  {property.size != null && property.size !== '' && (
                    <div>
                      <h3 className="font-semibold mb-1">{t('propertyDetailPage.lotSize')}</h3>
                      <p className="text-gray-700">{formatSize(property.size, property.size_unit)}</p>
                    </div>
                  )}
                  {property.bedrooms != null && property.bedrooms !== '' && (
                    <div>
                      <h3 className="font-semibold mb-1">{t('propertyDetailPage.bedrooms')}</h3>
                      <p className="text-gray-700">{Number(property.bedrooms)}</p>
                    </div>
                  )}
                  {property.bathrooms != null && property.bathrooms !== '' && (
                    <div>
                      <h3 className="font-semibold mb-1">{t('propertyDetailPage.bathrooms')}</h3>
                      <p className="text-gray-700">{Number(property.bathrooms)}</p>
                    </div>
                  )}
                  {(property.living_area_value != null && property.living_area_value !== '') && (
                    <div>
                      <h3 className="font-semibold mb-1">{t('propertyDetailPage.livingArea')}</h3>
                      <p className="text-gray-700">{formatSize(property.living_area_value, property.living_area_unit)}</p>
                    </div>
                  )}
                  {property.year_built != null && property.year_built !== '' && (
                    <div>
                      <h3 className="font-semibold mb-1">{t('propertyDetailPage.yearBuilt')}</h3>
                      <p className="text-gray-700">{Number(property.year_built)}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Land/lot access & utilities */}
              <div className="grid grid-cols-2 gap-6 border-t pt-6">
                {property.road_access != null && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.roadAccess')}</h3>
                    <p className="text-gray-700">{formatBoolean(property.road_access)}</p>
                  </div>
                )}
                {property.road_surface != null && property.road_surface !== '' && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.roadSurface')}</h3>
                    <p className="text-gray-700">{t(`propertyDetailPage.roadSurfaceValues.${String(property.road_surface).toLowerCase()}`) || property.road_surface}</p>
                  </div>
                )}
                {property.in_community != null && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.inCommunity')}</h3>
                    <p className="text-gray-700">{formatBoolean(property.in_community)}</p>
                  </div>
                )}
                {property.water_connection != null && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.waterConnection')}</h3>
                    <p className="text-gray-700">{formatBoolean(property.water_connection)}</p>
                  </div>
                )}
                {property.electricity != null && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.electricity')}</h3>
                    <p className="text-gray-700">{formatBoolean(property.electricity)}</p>
                  </div>
                )}
                {property.has_title != null && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.titleDeed')}</h3>
                    <p className="text-gray-700">{formatBoolean(property.has_title)}</p>
                  </div>
                )}
                {property.plot_number && (
                  <div>
                    <h3 className="font-semibold mb-1">{t('propertyDetailPage.plotNumber')}</h3>
                    <p className="text-gray-700">{property.plot_number}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Map + Directions */}
            {coords && (
              <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">{t('propertyDetailPage.location')}</h2>
                <div className="mb-4">
                  <MapComponent
                    height="300px"
                    center={[coords.lat, coords.lng]}
                    zoom={15}
                    markers={[
                      {
                        id: property.id,
                        lat: coords.lat,
                        lng: coords.lng,
                        title: property.title,
                        description: property.address || property.region,
                        color: '#3B82F6'
                      }
                    ]}
                    showControls={false}
                  />
                </div>
                {directionsLinks && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={directionsLinks.google}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                      📍 {t('propertyDetailPage.googleMaps')}
                    </a>
                    <a
                      href={directionsLinks.waze}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                    >
                      🧭 {t('propertyDetailPage.waze')}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4">{t('propertyDetailPage.contactInformation')}</h3>

                {property.sale_type_detail === 'for_sale_by_owner' && property.is_owner && (
                  <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                    {t('propertyDetailPage.ownerSaleNotice')}
                  </div>
                )}
                {property.user?.is_real_estate_business && (
                  <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    {t('propertyDetailPage.agentReportedNotice')}
                  </div>
                )}
                
                <div className="space-y-4 mb-6">
                  {property.contact_name != null && property.contact_name !== '' && (
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-500">👤</span>
                      <div>
                        <p className="text-sm text-gray-600">{t('propertyDetailPage.contactName')}</p>
                        <p className="font-medium text-gray-900">{property.contact_name}</p>
                      </div>
                    </div>
                  )}
                  {property.phone && (
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-500">📞</span>
                      <div>
                        <p className="text-sm text-gray-600">{t('propertyDetailPage.phone')}</p>
                        <p className="font-medium text-gray-900">{property.phone}</p>
                      </div>
                    </div>
                  )}
                  
                  {normalizedWhatsapp && (
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-500">💬</span>
                      <div>
                        <p className="text-sm text-gray-600">{t('propertyDetailPage.whatsapp')}</p>
                        <p className="font-medium text-gray-900">{normalizedWhatsapp}</p>
                      </div>
                    </div>
                  )}
                  
                  {property.email && (
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-500">📧</span>
                      <div>
                        <p className="text-sm text-gray-600">{t('propertyDetailPage.email')}</p>
                        <p className="font-medium text-gray-900">{property.email}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Contact person intentionally hidden per requirements */}
                  
                  {/* Fallback when no contact info is available */}
                  {!property.phone && !normalizedWhatsapp && !property.email && (
                    <div className="text-center py-4 bg-gray-50 rounded-lg">
                      <div className="text-gray-400 text-2xl mb-2">📞</div>
                      <p className="text-gray-600">{t('propertyDetailPage.noContactInfo')}</p>
                      <p className="text-sm text-gray-500">{t('propertyDetailPage.useMessageForm')}</p>
                    </div>
                  )}
                </div>

                {/* Contact Action Buttons */}
                <div className="space-y-3">
                  {property.phone && (
                    <button
                      onClick={() => handleContact('call')}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      📞 {t('propertyDetailPage.call')}
                    </button>
                  )}
                  
                  {normalizedWhatsapp && (
                    <button
                      onClick={() => handleContact('whatsapp')}
                      className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      💬 {t('propertyDetailPage.whatsapp')}
                    </button>
                  )}
                  
                  {property.email && (
                    <button
                      onClick={() => setShowContactForm(!showContactForm)}
                      className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      📧 {t('propertyDetailPage.sendMessage')}
                    </button>
                  )}
                </div>

                {/* Contact Form */}
                {showContactForm && (
                  <form onSubmit={handleSendMessage} className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('propertyDetailPage.subject')}
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={contactForm.subject}
                        onChange={handleContactFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('propertyDetailPage.message')}
                      </label>
                      <textarea
                        name="message"
                        value={contactForm.message}
                        onChange={handleContactFormChange}
                        rows="4"
                        maxLength="2000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t('propertyDetailPage.messagePlaceholder')}
                        required
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        {contactForm.message.length}/2000 {t('propertyDetailPage.characters')}
                      </p>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowContactForm(false)}
                        className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="submit"
                        disabled={isSendingMessage || !contactForm.message.trim()}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {isSendingMessage ? t('common.sending') : t('propertyDetailPage.sendMessage')}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Buy matching house – only for land/lot */}
              {isLandLot && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-semibold mb-2">{t('propertyDetailPage.buyMatchingHouseTitle')}</h3>
                  <p className="text-sm text-gray-600 mb-4">{t('propertyDetailPage.buyMatchingHouseSubtitle')}</p>
                  <a
                    href={homesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                  >
                    {t('propertyDetailPage.buyMatchingHouseButton')}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    {/* Lightbox Modal */}
    {isLightboxOpen && imageList[lightboxIndex] && (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={closeLightbox}>
        <button
          onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
          className="absolute top-4 right-4 text-white text-2xl px-3 py-1 bg-black/40 rounded"
          aria-label={t('common.close')}
        >
          ✕
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); gotoPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-3xl px-3 py-1 bg-black/40 rounded"
          aria-label={t('propertyDetailPage.prevImage')}
        >
          ‹
        </button>
        <img
          src={normalizeMediaUrl(imageList[lightboxIndex].image_url) || storageUrl(imageList[lightboxIndex].file_path)}
          alt={property.title}
          className="max-h-[90vh] max-w-[92vw] object-contain rounded"
          onClick={(e) => e.stopPropagation()}
        />
        <button
          onClick={(e) => { e.stopPropagation(); gotoNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-3xl px-3 py-1 bg-black/40 rounded"
          aria-label={t('propertyDetailPage.nextImage')}
        >
          ›
        </button>
      </div>
    )}
    {showReportModal && (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">{t('propertyDetailPage.reportThisListing')}</h3>
            <button
              onClick={() => setShowReportModal(false)}
              className="text-gray-500 hover:text-gray-700"
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>
          <form onSubmit={handleSubmitReport} className="px-6 py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('propertyDetailPage.reason')}</label>
              <select
                name="reason"
                value={reportForm.reason}
                onChange={handleReportChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              >
                <option value="sold">{t('propertyDetailPage.reasonSold')}</option>
                <option value="incorrect_information">{t('propertyDetailPage.reasonIncorrect')}</option>
                <option value="other">{t('propertyDetailPage.reasonOther')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('propertyDetailPage.additionalDetails')}
              </label>
              <textarea
                name="details"
                value={reportForm.details}
                onChange={handleReportChange}
                rows="4"
                maxLength="1000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder={t('propertyDetailPage.additionalDetailsPlaceholder')}
              />
              <p className="text-sm text-gray-500 mt-1">
                {reportForm.details.length}/1000 {t('propertyDetailPage.characters')}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowReportModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmittingReport}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isSubmittingReport ? t('common.sending') : t('propertyDetailPage.submitReport')}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </div>
  );
};

export default PropertyDetailPage;

